/**
 * dispatchSupabaseApi — Codex schema alignment (003_dispatch_p_system.sql)
 *
 * P source  : orders.p_units          (NOT orders.number_of_reels)
 * Batch ref : orders.batch_id         (NOT dispatch_batch_orders join table)
 * City agg  : dispatch_progress view  (city / total_p / remaining_p)
 *
 * dispatch_batch_orders is intentionally NOT used here.
 */
import { getSupabaseClient, isSupabaseConfigured } from "../lib/supabaseClient";
import type { Order } from "../store/types";

export const P_THRESHOLD = 20;

// ─── Types ────────────────────────────────────────────────────────────────────

/** Row returned by the dispatch_progress view */
export interface DispatchProgressRow {
  city: string;
  total_p: number;
  remaining_p: number;
}

/** Enriched city object combining view data + unbatched orders */
export interface CityProgress {
  city: string;
  total_p: number;
  remaining_p: number;
  order_count: number;
  orders: Order[];
  is_ready: boolean;
}

/** Matches Codex dispatch_batches schema */
export interface DispatchBatch {
  id: string;
  city: string;
  /** Codex column — canonical P total for this batch */
  total_p_units: number;
  order_count: number;
  /** Codex column — use "status" not "batch_status" */
  status: "draft" | "dispatched" | "completed";
  start_date: string | null;
  end_date: string | null;
  photographer_id: string | null;
  created_at: string;
  updated_at: string;
}

// ─── Fetch unbatched orders ───────────────────────────────────────────────────
// Uses orders.batch_id IS NULL — no reference to dispatch_batch_orders

export async function fetchUnbatchedOrders(): Promise<Order[]> {
  if (!isSupabaseConfigured()) throw new Error("Supabase not configured");
  const sb = getSupabaseClient();

  const { data, error } = await sb
    .from("orders")
    .select("*")
    .is("batch_id", null)
    .order("created_at", { ascending: true });

  if (error) throw error;
  return (data ?? []) as Order[];
}

// ─── Fetch city progress from view ───────────────────────────────────────────
// Reads dispatch_progress view: city / total_p / remaining_p

export async function fetchCityProgressView(): Promise<DispatchProgressRow[]> {
  if (!isSupabaseConfigured()) throw new Error("Supabase not configured");
  const sb = getSupabaseClient();

  const { data, error } = await sb
    .from("dispatch_progress")
    .select("city, total_p, remaining_p")
    .order("total_p", { ascending: false });

  if (error) throw error;
  return (data ?? []) as DispatchProgressRow[];
}

// ─── Fetch dispatch batches ───────────────────────────────────────────────────

export async function fetchDispatchBatches(): Promise<DispatchBatch[]> {
  if (!isSupabaseConfigured()) throw new Error("Supabase not configured");
  const sb = getSupabaseClient();

  const { data, error } = await sb
    .from("dispatch_batches")
    .select("id, city, total_p_units, order_count, status, start_date, end_date, photographer_id, created_at, updated_at")
    .order("created_at", { ascending: false });

  if (error) throw error;
  return (data ?? []) as DispatchBatch[];
}

// ─── Build CityProgress by combining view + unbatched orders ─────────────────

export function buildCityProgress(
  progressRows: DispatchProgressRow[],
  unbatchedOrders: Order[]
): CityProgress[] {
  // Group unbatched orders by city
  const ordersByCity: Record<string, Order[]> = {};
  for (const order of unbatchedOrders) {
    const city = order.selected_city || "Unknown";
    if (!ordersByCity[city]) ordersByCity[city] = [];
    ordersByCity[city].push(order);
  }

  return progressRows.map((row) => {
    const orders = ordersByCity[row.city] ?? [];
    return {
      city: row.city,
      total_p: row.total_p,
      remaining_p: row.remaining_p,
      order_count: orders.length,
      orders,
      is_ready: row.total_p >= P_THRESHOLD,
    };
  }).sort((a, b) => b.total_p - a.total_p);
}

// ─── Create dispatch batch ────────────────────────────────────────────────────
// Assigns orders via UPDATE orders.batch_id — no dispatch_batch_orders insert

export async function createDispatchBatch(
  city: string,
  orders: Order[]
): Promise<DispatchBatch> {
  if (!isSupabaseConfigured()) throw new Error("Supabase not configured");
  const sb = getSupabaseClient();

  // Sort earliest first
  const sorted = [...orders].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  );

  // Sum p_units (Codex) — NOT number_of_reels
  const totalPUnits = sorted.reduce((sum, o) => sum + (o.p_units ?? 0), 0);

  // 1. Insert batch record (Codex columns: total_p_units, status)
  const { data: batch, error: bErr } = await sb
    .from("dispatch_batches")
    .insert({
      city,
      total_p_units: totalPUnits,
      order_count: sorted.length,
      status: "draft",
    })
    .select("id, city, total_p_units, order_count, status, start_date, end_date, photographer_id, created_at, updated_at")
    .maybeSingle();

  if (bErr) throw bErr;
  if (!batch) throw new Error("Failed to create dispatch batch");

  const batchId = (batch as DispatchBatch).id;

  // 2. Assign orders by updating batch_id — NOT via dispatch_batch_orders
  const orderIds = sorted.map((o) => o.id);
  const { error: uErr } = await sb
    .from("orders")
    .update({ batch_id: batchId })
    .in("id", orderIds);

  if (uErr) throw uErr;

  return batch as DispatchBatch;
}

// ─── Fetch relay threads auto-created for a set of orders ────────────────────
// Called after batch creation to surface backend-generated conversation threads

export interface RelayThreadStub {
  id: string;
  order_id: string;
  subject: string;
  participant_type: string;
  created_at: string;
}

export async function fetchRelayThreadsForOrders(orderIds: string[]): Promise<RelayThreadStub[]> {
  if (!isSupabaseConfigured() || orderIds.length === 0) return [];
  const sb = getSupabaseClient();
  const { data } = await sb
    .from("conversation_threads")
    .select("id, order_id, subject, participant_type, created_at")
    .in("order_id", orderIds)
    .order("created_at", { ascending: false });
  return (data ?? []) as RelayThreadStub[];
}
